import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  changeDraftPhraseIdentity,
  confirmSpread,
  getBookDraftPhrases,
  getAllReadings,
  getCharReadings,
  getDictionary,
  getMultiDictionary,
  getBookSpreadList,
  getBookWithSpreadCounts,
  updateBookDetails,
  getPagePhotoUrl,
  getSpread,
  getSpreadDraftPhrases,
  getSpreadPhotos,
  markDraftPhraseVetted,
  mergeDraftPhrases,
  deleteDraftPhrase,
  insertDraftPhrase,
  reorderDraftPhrases,
  restoreSpreadDraft,
  restoreBookDraft,
  makeBookDraftSnapshot,
  replaceDraftPhraseCharacters,
  splitDraftPhrase,
  updateDraftPlacement,
  updateDraftPhrase,
} from '@chinese-reader/shared';
import { convertNumberedTones, lookupWithFallback } from '@chinese-reader/shared';
import type { SpreadDraftPhrase } from '@chinese-reader/shared';
import { supabase } from './supabase';
import { useNavigate, useParams } from 'react-router-dom';
import { composePinyin, gridKeyAction, isPinyinComplete, isSyllablePicked, shouldVetPhraseCommit } from './keyMap';
import { KEYS } from './platform';

type Mode = 'grid' | 'edit';
export type EditorField = 'chinese' | 'pinyin' | 'definition';

function errorText(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

function phraseLines(phrases: SpreadDraftPhrase[]) {
  return phrases.reduce<SpreadDraftPhrase[][]>((lines, phrase) => {
    const line = lines[lines.length - 1] ?? [];
    if (lines.length === 0) lines.push(line);
    line.push(phrase);
    if (phrase.line_break_after) lines.push([]);
    return lines;
  }, []).filter((line) => line.length > 0);
}

export default function ReviewSpread() {
  const { bookId = '', spreadId = '' } = useParams();
  const navigate = useNavigate();
  const [spread, setSpread] = useState<any>(null);
  const [phrases, setPhrases] = useState<SpreadDraftPhrase[]>([]);
  const [photos, setPhotos] = useState<Array<{ id: string; position: number; photo_url: string | null }>>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [spreads, setSpreads] = useState<any[]>([]);
  const [bookTitle, setBookTitle] = useState('');
  const [focused, setFocused] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const selectionAnchor = useRef(0);
  const [mode, setMode] = useState<Mode>('grid');
  const [draft, setDraft] = useState<SpreadDraftPhrase | null>(null);
  const [secondLook, setSecondLook] = useState<Set<string>>(new Set());
  const [entryField, setEntryField] = useState<EditorField>('chinese');
  const [awaitingLevel, setAwaitingLevel] = useState(false);
  const [status, setStatus] = useState('');
  const [failure, setFailure] = useState('');
  const gridRef = useRef<HTMLDivElement>(null);
  const chineseRef = useRef<HTMLInputElement>(null);
  const findRef = useRef<HTMLInputElement>(null);
  // Seeds the find field when `f` is pressed. Carries a nonce so pressing `f` again on a different
  // Phrase re-seeds — the value alone would not change when the same character comes round twice.
  const [findSeed, setFindSeed] = useState<{ value: string; nonce: number }>({ value: '', nonce: 0 });
  const dragIndex = useRef<number | null>(null);
  // A ref as well as state: the keydown handler closes over its render's values, so a state flag
  // alone would still let a second keypress through before React re-rendered. The ref decides, the
  // state only draws.
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const undoRef = useRef<Array<ReturnType<typeof makeSnapshot>>>([]);
  const redoRef = useRef<Array<ReturnType<typeof makeSnapshot>>>([]);
  const bulkUndoRef = useRef<ReturnType<typeof makeBookDraftSnapshot> | null>(null);

  // A bulk replace's undo restores the WHOLE Book from a snapshot, so it can only be offered while
  // nothing else has happened since. Any other write invalidates it: otherwise ⌘Z after a replace
  // and two edits reverts the edits too, silently — the same clobber migration 138 removed from
  // restore_spread_draft, at Book scale. IDEs refuse a stale undo rather than half-reverting; this
  // is the cheap version of refusing.
  const dropBulkUndo = () => { bulkUndoRef.current = null; };

  // `punctuation_after` rides along even though no Grid-mode key touches it: the restore deletes
  // and re-inserts every Placement on the Spread, so a column left out of the snapshot comes back
  // null. It is the one placement column that survives publish, so losing it is Reader-visible.
  function makeSnapshot() {
    // Phrases are deduplicated by identity; Placements are not. `phrases` here is the Spread's
    // PLACEMENT list, so a Phrase appearing three times on the Spread — 我 is unremarkable — would
    // otherwise appear three times in the snapshot, and the restore's ON CONFLICT DO UPDATE would
    // try to update one row several times in a single statement. Postgres refuses that outright:
    // "ON CONFLICT DO UPDATE command cannot affect row a second time", which made undo fail on any
    // Spread with a repeated Phrase — which is most of them.
    const byIdentity = new Map<string, SpreadDraftPhrase>();
    for (const phrase of phrases) {
      const key = `${phrase.chinese}\u0000${phrase.pinyin}`;
      if (!byIdentity.has(key)) byIdentity.set(key, phrase);
    }
    return {
      phrases: [...byIdentity.values()].map(({ chinese, pinyin, definition, assessment_level, is_new, vetted }) => ({ chinese, pinyin, definition, assessment_level, is_new, vetted })),
      placements: phrases.map(({ photo_id, chinese, pinyin, position, line_break_after, punctuation_after }) => ({ photo_id, chinese, pinyin, position, line_break_after, punctuation_after: punctuation_after ?? null })),
    };
  }

  const remember = () => {
    undoRef.current.push(makeSnapshot());
    redoRef.current = [];
  };

  const restore = async (snapshot: ReturnType<typeof makeSnapshot>) => {
    await restoreSpreadDraft(spreadId, snapshot);
    await reload();
  };

  const reload = async () => {
    const [nextSpread, nextPhrases, nextPhotos] = await Promise.all([
      getSpread(spreadId), getSpreadDraftPhrases(spreadId), getSpreadPhotos(spreadId),
    ]);
    setSpread(nextSpread);
    setPhrases(nextPhrases);
    setPhotos(nextPhotos);
    setPhotoUrls(await Promise.all(nextPhotos.map((photo) =>
      photo.photo_url ? getPagePhotoUrl(photo.photo_url) : Promise.resolve(''))));
  };

  useEffect(() => {
    void Promise.all([reload(), getBookSpreadList(bookId), getBookWithSpreadCounts(bookId)])
      .then(([, list, book]) => { setSpreads(list); setBookTitle(book.title); })
      .catch((error) => setFailure(errorText(error)));
  }, [bookId, spreadId]);

  useEffect(() => { setSecondLook(new Set()); setSelected(new Set()); undoRef.current = []; redoRef.current = []; bulkUndoRef.current = null; }, [spreadId]);

  // A structural correction can shrink the Spread under the cursor. Deleting the last Placement
  // leaves `focused` past the end, where every key that reads `phrases[focused]` silently does
  // nothing — a second `⌫` looks like the keyboard stopped responding — until an arrow re-clamps it.
  useEffect(() => {
    if (phrases.length && focused > phrases.length - 1) setFocused(phrases.length - 1);
  }, [phrases, focused]);

  // The panel tracks the focused Phrase continuously — it is a view of what is selected, not a
  // thing that opens. Only re-sync while Grid mode holds focus: in Edit mode `draft` is the
  // admin's in-flight copy, and a background reload must not overwrite what they are typing.
  useEffect(() => {
    if (mode === 'edit') return;
    setDraft(phrases[focused] ? { ...phrases[focused] } : null);
  }, [focused, phrases, mode]);

  // Only the grid is focused from here. Edit mode's destination belongs to the panel, which knows
  // which field was asked for — this used to also focus chinese, and since React runs child effects
  // before parent ones it overrode the panel every time, so `p` and `d` both landed on chinese.
  //
  // `spread` is in the deps because on first load this ran while the component was still rendering
  // "Loading Spread…", when gridRef.current is null: focus silently went nowhere and the grid had
  // to be clicked before the arrows did anything.
  useEffect(() => {
    if (mode === 'grid' && spread) gridRef.current?.focus();
  }, [mode, spread, spreadId]);

  const move = (delta: number) => setFocused((current) => {
    const next = Math.max(0, Math.min(phrases.length - 1, current + delta));
    selectionAnchor.current = next;
    setSelected(new Set());
    return next;
  });
  const selectMove = (delta: number) => {
    if (!phrases.length) return;
    const next = Math.max(0, Math.min(phrases.length - 1, focused + delta));
    setFocused(next);
    setSelected((current) => {
      const range = phrases.slice(Math.min(selectionAnchor.current, next), Math.max(selectionAnchor.current, next) + 1);
      return new Set([...current, ...range.map((phrase) => phrase.id)]);
    });
  };
  const moveLine = (delta: number) => {
    if (!phrases.length) return;
    const currentLine = phrases.slice(0, focused).filter((phrase) => phrase.line_break_after).length;
    const lines = phrases.reduce<number[]>((result, phrase, index) => {
      if (index === 0 || result.length === 0) result.push(index);
      if (phrase.line_break_after && index + 1 < phrases.length) result.push(index + 1);
      return result;
    }, []);
    const target = lines[Math.max(0, Math.min(lines.length - 1, currentLine + delta))];
    if (target !== undefined) setFocused(target);
  };

  // The panel is already on screen (ADR 0042), so this moves focus to a named field rather than
  // opening anything. `field` is handed to the panel, which focuses it once it has rendered.
  const openEditor = (field: EditorField) => {
    if (!phrases[focused]) return;
    setDraft({ ...phrases[focused] });
    setEntryField(field);
    setMode('edit');
  };

  // `a` then a digit sets the level from the grid. Two deliberate presses on a named Phrase is the
  // same weight as reaching a field with c/p/d, so it vets on the same reasoning (ADR 0042) — the
  // bare digit it replaced did not, because a single stray keypress is not an assertion.
  const setLevelFromGrid = async (level: number) => {
    dropBulkUndo();
    const phrase = phrases[focused];
    if (!phrase) return;
    const next = phrase.assessment_level === level ? null : level;
    setPhrases((current) => current.map((p) =>
      p.draft_phrase_id === phrase.draft_phrase_id ? { ...p, assessment_level: next } : p));
    try {
      await updateDraftPhrase(phrase.draft_phrase_id, { assessment_level: next });
      if (phrase.is_new && !phrase.vetted) await markDraftPhraseVetted(phrase.draft_phrase_id);
      await reload();
    } catch (error) { setFailure(errorText(error)); void reload(); }
  };

  const commit = async () => {
    dropBulkUndo();
    if (!draft) return;
    setFailure('');
    try {
      const original = phrases[focused];
      if (draft.chinese.trim().includes(' ')) {
        const dictionary = await getDictionary().catch(() => null);
        const fragments = draft.chinese.trim().split(/\s+/).map((chinese, index, all) => {
          const entry = dictionary ? lookupWithFallback(dictionary, chinese) : null;
          return {
            chinese,
            pinyin: entry ? convertNumberedTones(entry.pinyin) : '',
            definition: entry?.definition ?? null,
            line_break_after: index === all.length - 1 ? draft.line_break_after : false,
          };
        });
        remember();
        await splitDraftPhrase(draft.id, spreadId, fragments);
        await reload();
        setFocused(Math.min(focused, phrases.length - 1));
        setMode('grid');
        return;
      }
      const identityChanged = draft.chinese !== original.chinese || draft.pinyin !== original.pinyin;
      if (identityChanged) {
        await changeDraftPhraseIdentity(draft.id, spread.book_id, draft.chinese.trim(), draft.pinyin,
          draft.definition, draft.assessment_level);
        await reload();
        const saved = (await getSpreadDraftPhrases(spreadId)).find((phrase) => phrase.id === draft.id);
        // The identity helper may have merged this placement into another Phrase. A trigger-derived
        // definition or level is evidence the admin has not seen the new Dictionary values yet.
        if (saved && shouldVetPhraseCommit(original, draft, saved)) {
          await markDraftPhraseVetted(saved.draft_phrase_id);
          setSecondLook((current) => {
            const next = new Set(current);
            next.delete(saved.id);
            return next;
          });
        } else if (saved) {
          setSecondLook((current) => new Set(current).add(saved.id));
        }
      } else {
        await updateDraftPhrase(draft.draft_phrase_id, {
          definition: draft.definition, assessment_level: draft.assessment_level,
        });
        await markDraftPhraseVetted(draft.draft_phrase_id);
        setSecondLook((current) => {
          const next = new Set(current);
          next.delete(draft.id);
          return next;
        });
        await reload();
      }
      setMode('grid');
      setStatus('Phrase saved');
    } catch (error) { setFailure(errorText(error)); }
  };

  const confirm = async () => {
    const offender = phrases.findIndex((phrase) => !phrase.definition?.trim() || !isPinyinComplete(phrase.pinyin));
    if (offender >= 0) {
      setFocused(offender);
      setDraft({ ...phrases[offender] });
      setMode('edit');
      setFailure('Every Phrase needs pinyin and a definition before this Spread can be Confirmed.');
      return;
    }
    try {
      await confirmSpread(spreadId);
      // Cleared once the Spread is Confirmed, not on every attempt: the gate above returns without
      // confirming anything, and a Save refused for a missing definition must not also cost the
      // admin the undo history of the corrections they made before pressing it.
      undoRef.current = []; redoRef.current = [];
      const index = spreads.findIndex((item) => item.id === spreadId);
      const next = spreads[index + 1];
      if (next) navigate(`/book/${bookId}/spread/${next.id}`);
      else navigate(`/book/${bookId}`);
    } catch (error) { setFailure(errorText(error)); }
  };

  // The snapshot survives a failed action. Merge and split are several PostgREST calls with no
  // transaction around them, so a failure can leave the Spread half-changed — precisely the state
  // ⌘Z exists to repair (ADR 0040). Discarding the snapshot there trades one wasted undo press,
  // in the case where nothing was written, for the loss of the only route back.
  //
  // A second structural key is swallowed while the first is in flight. Each action snapshots and
  // acts on `phrases` from its own closure, so holding ⌫ — or pressing m twice before the reload
  // lands — ran the second against ids the first had already deleted. Queueing them was the
  // alternative and is worse for exactly the key that needs this most: held, ⌫ would delete every
  // Placement it buffered, and undo is per-action, so recovering means pressing ⌘Z as many times.
  //
  // Only structural keys are held. Navigation and the field jumps do not route through here, so the
  // screen stays responsive rather than feeling frozen.
  const structural = async (action: () => Promise<void>) => {
    if (busyRef.current) return;
    dropBulkUndo();
    busyRef.current = true;
    setBusy(true);
    remember();
    try { await action(); await reload(); }
    catch (error) { setFailure(errorText(error)); void reload().catch(() => undefined); }
    finally { busyRef.current = false; setBusy(false); }
  };

  // `m` merges forward, `⇧m` backward. Both express the same correction — the OCR split one Phrase
  // in two — so which neighbour you reach for depends only on where the focus happens to sit.
  //
  // The merged Phrase keeps the SECOND part's line break, whichever direction it came from: the
  // break belongs to whatever now ends the run.
  const mergeWith = (before: boolean) => {
    const first = before ? phrases[focused - 1] : phrases[focused];
    const second = before ? phrases[focused] : phrases[focused + 1];
    if (!first || !second) return;
    if (before) setFocused(focused - 1);
    void structural(() => mergeDraftPhrases(spreadId, first.id, second.id, {
      chinese: first.chinese + second.chinese,
      pinyin: [first.pinyin, second.pinyin].filter(Boolean).join(' '),
      definition: null,
      assessment_level: null,
      line_break_after: second.line_break_after,
    }).then(() => undefined));
  };

  const deleteFocused = () => {
    const current = phrases[focused];
    if (!current) return;
    void structural(() => deleteDraftPhrase(current.id));
  };

  const deleteSelected = () => {
    const ids = phrases.filter((phrase) => selected.has(phrase.id)).map((phrase) => phrase.id);
    if (ids.length === 0) return deleteFocused();
    void structural(async () => { for (const id of ids) await deleteDraftPhrase(id); setSelected(new Set()); });
  };

  // `n` inserts after the focused Phrase, `⇧n` before it. Inserting before matters when the OCR
  // dropped the first Phrase on a line — there is otherwise nothing to insert after.
  const addRelative = (before: boolean) => {
    void structural(async () => {
      const added = await insertDraftPhrase(spreadId, '', '', null, null);
      const ids = [...phrases.map((phrase) => phrase.id), added.id];
      ids.splice(ids.length - 1, 1);
      const at = before ? focused : Math.min(focused + 1, ids.length);
      ids.splice(at, 0, added.id);
      await reorderDraftPhrases(spreadId, ids);
      setFocused(at);
      setDraft(added);
      // Always chinese, whatever field the last c/p/d armed: a Phrase added by `n` has no
      // characters yet, so any other destination opens the panel on a field that cannot be filled.
      setEntryField('chinese');
      setMode('edit');
    });
  };

  const reorder = (delta: number) => {
    const target = focused + delta;
    if (target < 0 || target >= phrases.length) return;
    const ids = phrases.map((phrase) => phrase.id);
    [ids[focused], ids[target]] = [ids[target], ids[focused]];
    void structural(() => reorderDraftPhrases(spreadId, ids).then(() => { setFocused(target); }));
  };

  const reorderTo = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= phrases.length || to >= phrases.length) return;
    const ids = phrases.map((phrase) => phrase.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    void structural(() => reorderDraftPhrases(spreadId, ids).then(() => { setFocused(to); }));
  };

  const toggleLineBreak = () => {
    const current = phrases[focused];
    if (!current) return;
    void structural(() => updateDraftPlacement(current.id, { line_break_after: !current.line_break_after }));
  };

  const undo = (redo: boolean) => {
    if (busyRef.current) return;
    if (!redo && bulkUndoRef.current) {
      const snapshot = bulkUndoRef.current;
      bulkUndoRef.current = null;
      busyRef.current = true;
      setBusy(true);
      void restoreBookDraft(snapshot, bookId).then(reload)
        .catch((error) => { bulkUndoRef.current = snapshot; setFailure(errorText(error)); })
        .finally(() => { busyRef.current = false; setBusy(false); });
      return;
    }
    const source = redo ? redoRef : undoRef;
    const target = redo ? undoRef : redoRef;
    const snapshot = source.current.pop();
    if (!snapshot) return;
    target.current.push(makeSnapshot());
    busyRef.current = true;
    setBusy(true);
    void restore(snapshot)
      .catch((error) => { target.current.pop(); source.current.push(snapshot); setFailure(errorText(error)); })
      .finally(() => { busyRef.current = false; setBusy(false); });
  };

  // Bound to the page rather than to the grid element (see the listener below). Selecting text to
  // copy, clicking the photo, or anything else that moves focus off the grid used to kill every one
  // of these keys: the grid is a sibling of the editor panel, so its own handler only fired while
  // focus was inside it. That is the failure that made Spread navigation look intermittent, and
  // this generalises the fix instead of patching one more key at a time.
  const keyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (mode !== 'grid') return;
    const action = gridKeyAction(event, { awaitingLevel });
    if (action.type === 'undo') { event.preventDefault(); undo(action.redo); }
    else if (action.type === 'merge') { event.preventDefault(); mergeWith(action.before); }
    else if (action.type === 'delete') { event.preventDefault(); deleteSelected(); }
    else if (action.type === 'select') { event.preventDefault(); selectMove(action.delta); }
    else if (action.type === 'clearSelection') { if (selected.size) { event.preventDefault(); setSelected(new Set()); } }
    else if (action.type === 'add') { event.preventDefault(); addRelative(action.before); }
    else if (action.type === 'reorder') { event.preventDefault(); reorder(action.delta); }
    else if (action.type === 'lineBreak') { event.preventDefault(); toggleLineBreak(); }
    else if (action.type === 'move') { event.preventDefault(); move(action.delta); }
    else if (action.type === 'line') { event.preventDefault(); moveLine(action.delta); }
    else if (action.type === 'confirm') { event.preventDefault(); void confirm(); }
    else if (action.type === 'field') { event.preventDefault(); openEditor(action.field); }
    else if (action.type === 'find') {
      event.preventDefault();
      // Only a single-character Phrase seeds the field. The grid selects a Phrase, not a character,
      // so 上海人 has no single answer — and the field validates to one character, so seeding it
      // with three would put the panel straight into an error the admin did not cause.
      const chinese = phrases[focused]?.chinese ?? '';
      setFindSeed((current) => ({
        value: [...chinese].length === 1 ? chinese : '',
        nonce: current.nonce + 1,
      }));
      findRef.current?.focus();
    }
    else if (action.type === 'awaitLevel') { event.preventDefault(); setAwaitingLevel(true); }
    else if (action.type === 'cancelLevel') { setAwaitingLevel(false); }
    else if (action.type === 'level') { event.preventDefault(); setAwaitingLevel(false); void setLevelFromGrid(action.level); }
    else if (action.type === 'spread') { event.preventDefault(); goToSpread(action.delta); }
    else if (action.type === 'photo') {
      event.preventDefault();
      setSelectedPhoto((current) => (current + action.delta + photos.length) % Math.max(photos.length, 1));
    }
  };

  const goToSpread = (delta: number) => {
    const index = spreads.findIndex((item) => item.id === spreadId) + delta;
    const target = spreads[index];
    if (target) navigate(`/book/${bookId}/spread/${target.id}`);
  };

  // Moving between Spreads and Photos is a command about the *view*, not about the grid, so it is
  // bound to the page rather than to the grid element.
  //
  // The grid and the editor panel are siblings, so the grid's own handler only fires while focus is
  // inside the grid. Since ADR 0042 made the panel permanently visible, it is easy to be focused
  // there without noticing — click a pinyin pill, or the +, or a bulk-correction field — and every
  // one of these keys silently did nothing. That is what made navigation feel intermittent: it
  // depended on where focus happened to be, and a dimmed panel does not look focused.
  //
  // Events from a text field are ignored, or `,` and `.` could not be typed into a definition.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      // A text field owns every key it is handed, and the editor panel and bulk correction own
      // theirs: both bind arrows, Enter, Escape and Tab for their own purposes.
      const typing = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (typing) return;

      // Two tiers. Moving between Spreads and Photos is about the *view*, so it works from anywhere
      // that is not a text field — including the editor panel, where focus sits for most of a
      // review. Excluding the whole panel is what left [ and ] feeling unreliable: they worked from
      // the grid and died the moment you touched a pinyin pill.
      //
      // Everything else stays out of the panel and bulk correction, which bind arrows, Enter, Escape
      // and Tab for their own purposes.
      const action = gridKeyAction(event, {});
      const viewLevel = action.type === 'spread' || action.type === 'photo';
      if (!viewLevel && target?.closest?.('.editor-panel, .bulk-correction')) return;
      keyDown(event as unknown as KeyboardEvent<HTMLDivElement>);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!spread) return <main className="centered">Loading Spread…</main>;
  return <main className="review-page">
    <header className="bar">
      <div>
        <button onClick={() => navigate('/')}>Books</button>
        <BookTitle bookId={bookId} title={bookTitle} onRenamed={setBookTitle} />
      </div>
      <div className="bar-right">
        {/* Every Spread in reading order, so the admin can see where they are and jump anywhere.
            The keyboard was the only route until now, and a key map is invisible. */}
        <nav className="spread-trail" aria-label="Spreads">
          {spreads.map((item) => (
            <button
              key={item.id}
              className={item.id === spreadId ? 'trail-node here' : 'trail-node'}
              aria-current={item.id === spreadId ? 'page' : undefined}
              title={item.kind === 'cover' ? 'Cover' : 'Go to this Spread'}
              onClick={() => navigate(`/book/${bookId}/spread/${item.id}`)}
            >
              {item.kind === 'cover'
                ? 'Cover'
                : item.page_start == null
                  ? '—'
                  : item.page_end != null && item.page_end !== item.page_start
                    ? `${item.page_start}–${item.page_end}`
                    : `${item.page_start}`}
            </button>
          ))}
        </nav>
      </div>
    </header>
    {failure && <p className="error" role="alert">{failure}</p>}{status && <p className="success">{status}</p>}
    <div className="review-layout">
      <PhotoPane urls={photoUrls} selected={selectedPhoto} />
      <section className="grid-pane"><div className="mode-label">{mode === 'grid' ? 'GRID MODE' : 'EDIT MODE'}</div>
        <div ref={gridRef} className={busy ? "phrase-grid busy" : "phrase-grid"} tabIndex={0} aria-label="Phrase grid">
          {phraseLines(phrases).map((line) => <div className="phrase-line" key={line[0].id}>{line.map((phrase) => {
            const index = phrases.indexOf(phrase);
            // The drop reads the source from a ref, but Firefox refuses to start a drag whose
            // dragstart set no data at all, so the mouse fallback needs the setData anyway.
             return <button key={phrase.id} draggable className={`phrase-chip ${index === focused ? 'focused' : ''} ${selected.has(phrase.id) ? 'selected' : ''} ${phrase.is_new && !phrase.vetted ? 'new' : 'settled'} ${!phrase.definition ? 'missing' : ''} ${secondLook.has(phrase.id) ? 'second-look' : ''}`} onDragStart={(event) => { dragIndex.current = index; event.dataTransfer.setData('text/plain', phrase.id); }} onDragOver={(event) => event.preventDefault()} onDrop={() => { if (dragIndex.current !== null) reorderTo(dragIndex.current, index); dragIndex.current = null; }} onClick={(event) => { if (event.shiftKey) { const start = selectionAnchor.current; const range = phrases.slice(Math.min(start, index), Math.max(start, index) + 1); setSelected(new Set(range.map((item) => item.id))); } else { setSelected(new Set()); selectionAnchor.current = index; } setFocused(index); gridRef.current?.focus(); }}>
              <span className="chip-pinyin">{phrase.pinyin || '—'}</span>
              <span className="chip-chinese">{phrase.chinese}</span>
              <span className="chip-level">{phrase.assessment_level ?? '–'}</span>
            </button>;
          })}</div>)}
        </div>
        {awaitingLevel
          ? <p className="key-help arming">Level: press <kbd>1</kbd>–<kbd>9</kbd>, or <kbd>0</kbd> for 10 — any other key cancels</p>
          : <dl className="key-legend">
              <div><dt>Move</dt><dd><kbd>←</kbd><kbd>→</kbd> Phrase · <kbd>↑</kbd><kbd>↓</kbd> line · <kbd>[</kbd><kbd>]</kbd> Spread · <kbd>,</kbd><kbd>.</kbd> Photo</dd></div>
              <div><dt>Edit</dt><dd><kbd>c</kbd> chinese · <kbd>p</kbd> pinyin · <kbd>d</kbd> definition · <kbd>a</kbd> then a digit sets level · <kbd>f</kbd> find &amp; replace</dd></div>
              <div><dt>Structure</dt><dd><kbd>m</kbd>/<kbd>{KEYS.shift}m</kbd> merge next/previous · <kbd>n</kbd>/<kbd>{KEYS.shift}n</kbd> new Phrase after/before · <kbd>{KEYS.del}</kbd> delete · <kbd>{KEYS.alt}</kbd><kbd>←</kbd><kbd>→</kbd> reorder · <kbd>/</kbd> line break</dd></div>
              <div><dt>Select</dt><dd><kbd>{KEYS.shift}</kbd>+arrows extend · <kbd>{KEYS.shift}</kbd>click for a range · <kbd>Esc</kbd> clear · <kbd>{KEYS.del}</kbd> deletes the whole selection</dd></div>
              <div><dt>Undo</dt><dd><kbd>{KEYS.mod}+Z</kbd> · <kbd>{KEYS.shift}+{KEYS.mod}+Z</kbd> redo · <kbd>{KEYS.mod}+{KEYS.enter}</kbd> save Spread &amp; next</dd></div>
            </dl>}
       </section>
      {draft && <Editor draft={draft} setDraft={setDraft} chineseRef={chineseRef} onCommit={() => void commit()} onCancel={() => void commit()} editing={mode === 'edit'} entryField={entryField} returnToGrid={() => gridRef.current?.focus()} />}
       <BulkCorrection findRef={findRef} seed={findSeed} returnToGrid={() => gridRef.current?.focus()} bookId={bookId} spreadId={spreadId} onApplied={(snapshot, count) => { bulkUndoRef.current = snapshot; setStatus(`Replaced ${count} ${count === 1 ? 'Phrase' : 'Phrases'} · ${KEYS.mod}+Z reverts this, until your next edit`); void reload(); }} />
    </div>
  </main>;
}

// The Book's title, editable in place — click it and it becomes a field, the way a document title
// does. It is the one piece of Book metadata worth changing without leaving the Spread you are
// reviewing, because a wrong title is usually noticed while reading the pages rather than before.
function BookTitle({ bookId, title, onRenamed }: { bookId: string; title: string; onRenamed: (value: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  const [failure, setFailure] = useState('');

  useEffect(() => { setValue(title); }, [title]);

  const commit = async () => {
    setEditing(false);
    const next = value.trim();
    // An empty title is a mis-click, not an intention, and Books are found by name.
    if (!next || next === title) { setValue(title); return; }
    onRenamed(next);
    try {
      await updateBookDetails(bookId, { title: next });
    } catch (error) {
      onRenamed(title);
      setValue(title);
      setFailure(error instanceof Error ? error.message : String(error));
    }
  };

  if (!editing) {
    return (
      <h1>
        <button className="title-button" title="Rename this Book" onClick={() => setEditing(true)}>
          {title || 'Untitled'}
        </button>
        {failure && <span className="error"> {failure}</span>}
      </h1>
    );
  }

  return (
    <h1>
      <input
        className="title-input"
        autoFocus
        value={value}
        aria-label="Book title"
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.nativeEvent.isComposing) return;
          if (event.key === 'Enter') { event.preventDefault(); void commit(); }
          // Esc abandons the rename rather than saving it — the one place on this screen where
          // leaving is not the commit, because a half-typed title is not a correction.
          if (event.key === 'Escape') { event.preventDefault(); setValue(title); setEditing(false); }
        }}
      />
    </h1>
  );
}

function PhotoPane({ urls, selected }: { urls: string[]; selected: number }) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, [selected]);
  return <section className="photo-pane" onWheel={(event) => { event.preventDefault(); setZoom((value) => Math.max(1, Math.min(4, value - event.deltaY / 500))); }} onPointerMove={(event) => { if (dragStart) setOffset({ x: event.clientX - dragStart.x, y: event.clientY - dragStart.y }); }} onPointerUp={() => setDragStart(null)}>
    <h2>Photo {selected + 1} of {Math.max(urls.length, 1)}</h2>{urls[selected] && <img src={urls[selected]} alt={`Photo ${selected + 1}`} onPointerDown={(event) => setDragStart({ x: event.clientX - offset.x, y: event.clientY - offset.y })} style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`, transformOrigin: 'top left' }} />}
  </section>;
}

function BulkCorrection({ findRef, seed, returnToGrid, bookId, spreadId, onApplied }: { findRef: React.RefObject<HTMLInputElement | null>; seed: { value: string; nonce: number }; returnToGrid: () => void; bookId: string; spreadId: string; onApplied: (snapshot: ReturnType<typeof makeBookDraftSnapshot>, count: number) => void }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [scope, setScope] = useState<'spread' | 'book'>('spread');
  const [candidates, setCandidates] = useState<SpreadDraftPhrase[]>([]);

  // Seed on every `f`, and select what is there so the first keystroke replaces it rather than
  // appending to a character the admin did not type.
  useEffect(() => {
    if (!seed.nonce) return;
    setFrom(seed.value);
    setCandidates([]);
    findRef.current?.select();
  }, [seed.nonce]);
  const [replacements, setReplacements] = useState<Array<{ draft_phrase_id: string; chinese: string; pinyin: string }>>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const preview = async () => {
    setError('');
    if ([...from].length !== 1 || [...to].length !== 1) { setError('Enter one Character in each field.'); return; }
    try {
      const all = await getBookDraftPhrases(bookId);
      const affected = all.filter((phrase) => (scope === 'book' || phrase.spread_id === spreadId) && phrase.chinese.includes(from));
      const dictionary = await getDictionary();
      const next = affected.map((phrase) => {
        const chinese = phrase.chinese.split(from).join(to);
        const entry = lookupWithFallback(dictionary, chinese);
        return { draft_phrase_id: phrase.draft_phrase_id, chinese, pinyin: entry ? convertNumberedTones(entry.pinyin) : phrase.pinyin };
      });
      setCandidates(affected);
      setReplacements([...new Map(next.map((item) => [item.draft_phrase_id, item])).values()]);
    } catch (cause) { setError(errorText(cause)); }
  };

  const apply = async () => {
    if (!replacements.length) return;
    setBusy(true); setError('');
    try {
      const snapshot = makeBookDraftSnapshot(await getBookDraftPhrases(bookId));
      await replaceDraftPhraseCharacters(bookId, scope === 'spread' ? spreadId : null, replacements);
      onApplied(snapshot, replacements.length); setCandidates([]); setReplacements([]); setFrom(''); setTo('');
    } catch (cause) { setError(errorText(cause)); }
    finally { setBusy(false); }
  };

  // Esc leaves for the grid, as it does from the editor panel. Every other surface on this screen
  // hands focus back that way, and without it the only route out of here was the mouse.
  return <section className="bulk-correction" onKeyDown={(event) => {
    if (event.key !== 'Escape' || event.nativeEvent.isComposing) return;
    event.preventDefault();
    returnToGrid();
  }}>
    <h2>Bulk Correction</h2>
    {/* Says what this is FOR, not what it does. The panel is easy to mistake for a way to fix every
        occurrence of one Phrase — which is not what it is: that Phrase is a single deduplicated row
        already. This exists for the opposite shape, one wrong character spread across Phrases that
        are otherwise unrelated (issue 06, ADR 0038). */}
    <p className="panel-hint muted">
      For one character misread across <em>distinct</em> Phrases — 设有, 设人, 设关系 all needing 设 → 沒.
      A single Phrase misread the same way everywhere is already one row: edit it in the panel above.
    </p>
    <div className="bulk-fields"><label>Find Character<input ref={findRef} value={from} maxLength={2} onChange={(event) => setFrom(event.target.value)} /></label><label>Replace with<input value={to} maxLength={2} onChange={(event) => setTo(event.target.value)} /></label></div>
    <div className="scope-toggle"><button type="button" className={`scope ${scope === 'spread' ? 'active' : ''}`} onClick={() => setScope('spread')}>This Spread</button><button type="button" className={`scope ${scope === 'book' ? 'active' : ''}`} onClick={() => setScope('book')}>Whole Book</button></div>
    <button type="button" onClick={() => void preview()} disabled={busy}>Preview</button>
    {error && <p className="error" role="alert">{error}</p>}
    {candidates.length > 0 && <div className="replace-preview"><p>{candidates.length} affected Phrase{candidates.length === 1 ? '' : 's'}:</p><ul>{candidates.map((phrase) => <li key={phrase.id}><strong>{phrase.chinese}</strong> → {phrase.chinese.split(from).join(to)}</li>)}</ul><button type="button" onClick={() => void apply()} disabled={busy}>Apply correction</button></div>}
  </section>;
}

// Digits jump to an assessment level, but only from outside a text field: `convertNumberedTones`
// makes `she4` a supported way to type pinyin, so inside an input a digit is a tone number
// (ADR 0040). The pills remain controls rather than digit shortcuts, so numbered-tone entry stays
// available in the custom text field.
export function Editor({ draft, setDraft, chineseRef, onCommit, onCancel, editing = true, entryField = 'chinese', returnToGrid }: { draft: SpreadDraftPhrase; setDraft: (value: SpreadDraftPhrase) => void; chineseRef: React.RefObject<HTMLInputElement | null>; onCommit: () => void; onCancel: () => void; editing?: boolean; entryField?: EditorField; returnToGrid?: () => void }) {
  const [rows, setRows] = useState<string[][]>([]);
  const [perCharacter, setPerCharacter] = useState(false);
  const [customPinyin, setCustomPinyin] = useState(false);
  // `+` is an escape hatch for one Phrase, not a mode. The panel is a single instance that
  // re-renders for whichever Phrase is focused rather than remounting per Phrase, so without this
  // the text field stayed open for every Phrase visited afterwards. Reset when the Phrase changes,
  // and when the panel becomes live again, so entering a field always starts from the readings.
  const [cell, setCell] = useState<{ row: number; col: number }>({ row: 0, col: 0 });
  const [activeLevel, setActiveLevel] = useState(0);
  // Keyed by row:col so an arrow can move DOM focus, not just the roving tabIndex — changing which
  // pill is tabbable does nothing on its own.
  const cellRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const pinyinRegion = useRef<HTMLSpanElement>(null);
  const levelRegion = useRef<HTMLDivElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);
  const addPinyinRef = useRef<HTMLButtonElement>(null);
  const definitionRef = useRef<HTMLInputElement>(null);
  const firstPillRef = useRef<HTMLButtonElement>(null);

  // c/p/d each name a destination; focus lands there rather than always on chinese.
  useEffect(() => { setCustomPinyin(false); }, [draft.id, editing]);

  // One reading means there is nothing to choose, so choosing it is not a decision the admin should
  // have to make — the mobile review screen has always filled these in. Applied only while the panel
  // is live, so it lands as part of an edit the admin is already making: ADR 0042 makes leaving the
  // panel the commit, and this rides that rather than writing behind their back.
  //
  // Only when the pinyin is missing or half-composed. A reading already chosen is theirs to keep,
  // even where CC-CEDICT offers just one alternative.
  useEffect(() => {
    if (!editing || !rows.length) return;
    if (isPinyinComplete(draft.pinyin)) return;
    if (!rows.every((options) => options.length === 1)) return;
    const only = rows.map((options) => options[0]).join(' ');
    if (only !== draft.pinyin) setDraft({ ...draft, pinyin: only });
  }, [editing, rows, draft.pinyin]);

  // Land the roving focus on whatever the Phrase already carries, so `p` opens on the current
  // reading rather than the first one — the panel should agree with the card, always.
  const segments = draft.pinyin ? draft.pinyin.split(' ') : [];
  const isPicked = (row: number, option: string) => isSyllablePicked(perCharacter, draft.pinyin, row, option);

  useEffect(() => {
    const row = rows.findIndex((options, index) =>
      options.some((option) => isSyllablePicked(perCharacter, draft.pinyin, index, option)));
    if (row >= 0) {
      const col = rows[row].findIndex((option) => isSyllablePicked(perCharacter, draft.pinyin, row, option));
      setCell({ row, col: Math.max(col, 0) });
    } else {
      setCell({ row: 0, col: 0 });
    }
    setActiveLevel(draft.assessment_level ? draft.assessment_level - 1 : 0);
  }, [rows, perCharacter, draft.pinyin, draft.assessment_level]);

  // Picking replaces one syllable when the rows are per-character, or the whole reading when they
  // are not. Missing segments are padded so picking row 2 before row 1 still composes correctly.
  const FIELDS = ['chinese', 'pinyin', 'definition', 'level'] as const;
  type Field = (typeof FIELDS)[number];

  const focusField = (field: Field) => {
    if (field === 'chinese') return chineseRef.current?.focus();
    if (field === 'definition') return definitionRef.current?.focus();
    if (field === 'level') return levelRefs.current[activeLevel]?.focus();
    if (customPinyin) return customInputRef.current?.focus();
    return cellRefs.current.get(`${cell.row}:${cell.col}`)?.focus();
  };

  const currentField = (): Field => {
    const active = document.activeElement;
    if (pinyinRegion.current?.contains(active)) return 'pinyin';
    if (levelRegion.current?.contains(active)) return 'level';
    if (active === definitionRef.current) return 'definition';
    return 'chinese';
  };

  const moveField = (delta: number) => {
    const next = (FIELDS.indexOf(currentField()) + delta + FIELDS.length) % FIELDS.length;
    focusField(FIELDS[next]);
  };

  const pick = (row: number, option: string) =>
    setDraft({ ...draft, pinyin: composePinyin(perCharacter, draft.chinese, draft.pinyin, row, option) });

  useEffect(() => {
    if (!editing) return;
    focusField(entryField === 'pinyin' ? 'pinyin' : entryField === 'definition' ? 'definition' : 'chinese');
  }, [editing, entryField, draft.id]);
  const levelRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Rows, not a flat list. CC-CEDICT either knows the whole Phrase — one row of complete readings —
  // or it does not, in which case each character contributes its own row and the admin composes the
  // pinyin syllable by syllable. Flattening the two cases together was the bug behind pills never
  // looking selected: a whole-Phrase pinyin can never equal a single character's reading, and
  // clicking one replaced the entire pinyin with that one syllable.
  useEffect(() => { void getMultiDictionary().then((dict) => {
    const phraseReadings = getAllReadings(dict, draft.chinese);
    if (phraseReadings.length) { setRows([phraseReadings]); setPerCharacter(false); return; }
    setRows([...draft.chinese].map((char) => [...new Set(getCharReadings(dict, char))]));
    setPerCharacter(true);
  }).catch(() => { setRows([]); setPerCharacter(false); }); }, [draft.chinese]);
  useEffect(() => { if (!draft.definition) void getDictionary().then((dict) => {
    const fallback = dict.get(draft.chinese)?.definition;
    if (fallback) setDraft({ ...draft, definition: fallback });
  }).catch(() => undefined); }, [draft.chinese]);
  return <aside className={editing ? "editor-panel editing" : "editor-panel"} onKeyDown={(event) => { if (event.nativeEvent.isComposing) return; if (event.key === 'Tab') { event.preventDefault(); moveField(event.shiftKey ? -1 : 1); return; } if (event.key === 'Escape') { event.preventDefault(); onCancel(); returnToGrid?.(); } else if (event.key === 'Enter') { if (event.target instanceof HTMLButtonElement) return; event.preventDefault(); onCommit(); returnToGrid?.(); } else if (/^[0-9]$/.test(event.key) && !(event.target instanceof HTMLInputElement)) { event.preventDefault(); const level = event.key === '0' ? 10 : Number(event.key); setDraft({ ...draft, assessment_level: level }); } }}><h2>Edit Phrase</h2><p className="panel-hint muted">{editing ? 'Editing · Tab between fields · arrows within pinyin · Enter or Space picks a reading · Esc leaves, saving' : 'From the grid: c chinese · p pinyin · d definition · a then a digit for level'}</p>
    <label>Chinese<input className="chinese-input" ref={chineseRef} value={draft.chinese} onChange={(e) => setDraft({ ...draft, chinese: e.target.value })} /></label>
     <label>Pinyin{customPinyin
      ? <input ref={customInputRef} autoFocus value={draft.pinyin} onChange={(e) => setDraft({ ...draft, pinyin: e.target.value })} onBlur={() => setDraft({ ...draft, pinyin: convertNumberedTones(draft.pinyin) })} />
      : <span className="pinyin-rows" ref={pinyinRegion}>
          {rows.map((options, row) => (
            <span className="pill-group" key={row}>
              {perCharacter && <span className="row-label">{[...draft.chinese][row]}</span>}
              {options.map((option, col) => (
                <button
                  type="button"
                  key={option}
                  ref={(element) => { cellRefs.current.set(`${row}:${col}`, element); if (row === cell.row && col === cell.col) firstPillRef.current = element; }}
                  className={isPicked(row, option) ? 'selected' : ''}
                  tabIndex={row === cell.row && col === cell.col ? 0 : -1}
                  onFocus={() => setCell({ row, col })}
                  onKeyDown={(event) => {
                    // Arrows walk the grid: across a character's readings, down to the next
                    // character. Tab is left alone so it still leaves for the next field.
                    const move = { ArrowLeft: [0, -1], ArrowRight: [0, 1], ArrowUp: [-1, 0], ArrowDown: [1, 0] }[event.key];
                    if (!move) return;
                    event.preventDefault();
                    if (move[0] === 1 && row === rows.length - 1) { addPinyinRef.current?.focus(); return; }
                    if (move[1] === 1 && col === rows[row].length - 1) { addPinyinRef.current?.focus(); return; }
                    const nextRow = Math.min(Math.max(row + move[0], 0), rows.length - 1);
                    const nextCol = Math.min(Math.max(col + move[1], 0), rows[nextRow].length - 1);
                    setCell({ row: nextRow, col: nextCol });
                    cellRefs.current.get(`${nextRow}:${nextCol}`)?.focus();
                  }}
                  onClick={() => pick(row, option)}
                >{option}</button>
              ))}
            </span>
          ))}
          {/* Inside a pill-group so it picks up the neutral pill styling. On its own it fell back to
              the base `button` rule, which paints the primary colour and made it read as selected —
              the same trap the pills themselves were in. One tab stop, not part of the roving
              group: a single control between pinyin and definition is not the dozen that issue 05
              objected to, and the escape hatch has to stay reachable from the keyboard. */}
          <span className="pill-group"><button type="button" ref={addPinyinRef} className="add-pinyin" tabIndex={-1} onKeyDown={(event) => { if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') { event.preventDefault(); cellRefs.current.get(`${rows.length - 1}:0`)?.focus(); } }} onClick={() => setCustomPinyin(true)}>+</button></span>
        </span>}</label>
    <label>Definition<input ref={definitionRef} value={draft.definition ?? ''} onChange={(e) => setDraft({ ...draft, definition: e.target.value })} /></label>
     <fieldset><legend>Assessment level</legend><div className="pill-group" ref={levelRegion}>{Array.from({ length: 10 }, (_, index) => index + 1).map((level, index) => <button type="button" key={level} ref={(element) => { levelRefs.current[index] = element; }} tabIndex={index === activeLevel ? 0 : -1} onFocus={() => setActiveLevel(index)} onKeyDown={(event) => { if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); const next = (index + (event.key === 'ArrowLeft' ? -1 : 1) + 10) % 10; setActiveLevel(next); levelRefs.current[next]?.focus(); } }} className={draft.assessment_level === level ? 'selected' : ''} onClick={() => setDraft({ ...draft, assessment_level: draft.assessment_level === level ? null : level })}>{level}</button>)}</div></fieldset>
    <div className="editor-actions"><button tabIndex={-1} onClick={onCommit}>Save and vet</button><button type="button" tabIndex={-1} onClick={onCancel}>Cancel</button></div>
  </aside>;
}
